import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Textarea } from "../../../components/Textarea";
import { Modal } from "../../../components/modal/Modal";
import { Info } from "../../../components/Info";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useDrawersContext } from "../../tasks/drawers-context";
import isFunction from "lodash/isFunction";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";

const OTHER_REASON = "__other__";

export const KanbanCardMovingReasonModal: React.FC = () => {
  const [reasonMessage, setReasonMessage] = useState("");
  const [selectedReason, setSelectedReason] = useState<string>();

  const { t } = useTranslation();
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const {
    kanbanCardMovingReasonVisible,
    setKanbanCardMovingReasonVisible,
    onDragKanbanCardWithReasonMessage,
    setOnDragKanbanCardWithReasonMessage,
    kanbanCardMovingReasonStageType,
  } = useDrawersContext();

  const presets = useMemo(
    () =>
      (kanban?.closingReasons || [])
        .filter((r) => r.stageType === kanbanCardMovingReasonStageType)
        .map((r) => r.label),
    [kanban?.closingReasons, kanbanCardMovingReasonStageType],
  );

  const isTyping = !presets.length || selectedReason === OTHER_REASON;
  const reason = isTyping ? reasonMessage.trim() : selectedReason || "";

  const localOnClose = () => {
    setKanbanCardMovingReasonVisible(false);
    setOnDragKanbanCardWithReasonMessage(null);
  };

  const onSubmit = () => {
    if (!isFunction(onDragKanbanCardWithReasonMessage)) {
      return;
    }

    onDragKanbanCardWithReasonMessage(reason);
    setKanbanCardMovingReasonVisible(false);
    setOnDragKanbanCardWithReasonMessage(null);
  };

  useEffect(() => {
    if (kanbanCardMovingReasonVisible) {
      setReasonMessage("");
      setSelectedReason(undefined);
    }
  }, [kanbanCardMovingReasonVisible]);

  return (
    <Modal
      open={kanbanCardMovingReasonVisible}
      onClose={localOnClose}
      title={t("deals.movingReason.modal.title")}
    >
      <Flex flexDirection="column" gap="m" width={"100%"}>
        <Info info={t("deals.movingReason.info")} />

        {presets.length ? (
          <AtiraSelect
            title={t("deals.movingReason.select.title")}
            value={selectedReason}
            onChange={setSelectedReason}
            options={[
              ...presets.map((label) => ({ label, value: label })),
              {
                label: t("deals.movingReason.select.other"),
                value: OTHER_REASON,
              },
            ]}
            required
          />
        ) : null}

        {!presets.length && !isMember ? (
          <Info info={t("deals.movingReason.noPresets.ownerInfo")} />
        ) : null}

        {isTyping ? (
          <Textarea
            title={t("deals.movingReason.description")}
            value={reasonMessage}
            onChange={(e) => setReasonMessage(e.currentTarget.value)}
            maxLength={Lengths.LONG}
            required
          />
        ) : null}

        <Button
          disabled={isTyping ? reason.length < 3 : !reason}
          onClick={onSubmit}
          margin="none"
        >
          {t("common.confirm")}
        </Button>
      </Flex>
    </Modal>
  );
};
